import { useEffect, useMemo } from 'react';

import { useNavigationStack } from '../../../context/NavigationStackContext';
import { useProgramItemExercises } from '../../../hooks/useProgramItemExercises';
import {
  useLazyGetProgramItemById,
  usePostPutProgramItem,
} from '../../../redux/api/programsAPI';
import { useGetMyDetails } from '../../../redux/api/users/usersApi';
import { Paths } from '../../../shared/paths';
import { IProgramItem } from '../../../types/interfaces/program-item';
import { orderProgramItem } from '../../../utils/training/set-utils';

export function useEditProgramItemInit(programItemId?: string) {
  const queryParams = new URLSearchParams(location.search);
  const defaultProgramCollectionId = queryParams.get('programCollectionId');
  const { back, navigate } = useNavigationStack();

  const [getProgramItem, { data: programItem, isFetching, error }] =
    useLazyGetProgramItemById();
  const { data: user } = useGetMyDetails();
  const [postPutProgramItem, { isLoading: isSaving }] = usePostPutProgramItem();
  const { exercises } = useProgramItemExercises({
    programItemId,
  });
  // const { navigate } = useNavigationStack();

  // useNotFound(!!error);

  useEffect(() => {
    if (programItemId) getProgramItem(programItemId);
  }, [programItemId]);

  const orderedProgramItem: IProgramItem = useMemo(() => {
    if (!programItem || !exercises) return null;

    if (programItem && !programItem.permissions.canEdit)
      navigate(Paths.programs.toViewItemId(programItem.id));

    return orderProgramItem(programItem, exercises);
  }, [programItem, exercises]);

  const initialValues: IProgramItem = useMemo(
    () => ({
      title: orderedProgramItem?.title || '',
      description: orderedProgramItem?.description || '',
      programCollectionId:
        orderedProgramItem?.programCollectionId ||
        (!orderedProgramItem && defaultProgramCollectionId) ||
        null,
      programSets: orderedProgramItem?.programSets,
    }),
    [orderedProgramItem, defaultProgramCollectionId],
  );

  function onSubmit(values) {
    return postPutProgramItem({
      id: programItemId,
      body: getBodyForSubmit(values),
    })
      .unwrap()
      .then((response) => back())
      .catch((e) => {
        console.log(e);
      });
  }

  function getBodyForSubmit(value: IProgramItem): IProgramItem {
    return {
      ...value,
      programSets: value.programSets.map((set) => {
        const { exercise, ...other } = set;

        return {
          ...other,
          programItemId: other.programItemId ?? programItemId ?? null,
          programSetItems: other.programSetItems.map((item) => ({
            ...item,
            sets: item.sets ? +item.sets : null,
            programSetItemOptions: item.programSetItemOptions.map((option) => ({
              ...option,
              value: option.value ? +option.value : null,
            })),
          })),
        };
      }),
    };
  }

  return {
    user,
    initialValues,
    programItem,
    isSaving,
    onSubmit,
  };
}
